/**
 * Yard API Service Layer
 *
 * Handles all yard and slot management API calls.
 */

import api from './api';
import type { YardSlot, YardStats, YardEquipmentListItem, AssignableTask, AvailableChassis } from '../types/yard';

// Backend base URL (without /api) for resolving static upload paths like /uploads/xxx.jpg
const BACKEND_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:4000/api').replace(/\/api$/, '');

/** Convert a relative upload path (e.g. /uploads/xxx.jpg) to an absolute URL. */
function toAbsoluteUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BACKEND_BASE}${path}`;
}

/** Normalize lastAction photoUrl from relative to absolute so <img src> works */
function normalizeSlotPhotoUrls(slot: YardSlot): YardSlot {
  if (slot.lastAction?.photoUrl) {
    return {
      ...slot,
      lastAction: { ...slot.lastAction, photoUrl: toAbsoluteUrl(slot.lastAction.photoUrl) },
    };
  }
  return slot;
}

// ==================== Types ====================

export interface Yard {
  id: number;
  name: string;
  short_name?: string;
  location: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  rows_count: number;
  cols_count: number;
  total_capacity: number;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  operating_hours?: string;
  status: 'active' | 'inactive' | 'maintenance';
  notes?: string;
  /**
   * This yard is where drivers return at the end of a shift. Used as the
   * destination of the end-of-shift empty run when no next task follows.
   * At most one yard per company can carry this flag.
   */
  is_company_base?: boolean;
  /** Read-only — filled in by the backend's geocoder from the address. */
  latitude?: number | string | null;
  longitude?: number | string | null;
  total_slots?: number;
  unused_slots?: number;
  available_slots?: number;
  occupied_slots?: number;
  locked_slots?: number;
}

/** Fields the backend accepts on create/update (see yard.model.js allowedFields) */
type YardWritableField =
  | 'name' | 'short_name' | 'location'
  | 'address' | 'city' | 'state' | 'zip_code'
  | 'rows_count' | 'cols_count'
  | 'contact_name' | 'contact_phone' | 'contact_email'
  | 'operating_hours' | 'status' | 'notes'
  | 'is_company_base';

/**
 * Create/update payload. Optional fields accept `null` so an edit can clear them —
 * the model skips `undefined` values, so `undefined` means "leave unchanged".
 */
export type YardInput = {
  [K in YardWritableField]?: Yard[K] | null;
};

interface SlotUpdateData {
  new_slot_id?: string;
  status?: string;
  occupation_type?: 'task' | 'chassis' | null;
  task_id?: number | null;
  chassis_id?: number | null;
  external_chassis_number?: string | null;
  external_chassis_company?: string | null;
  external_chassis_size?: string | null;
  notes?: string;
}

interface OccupySlotData {
  occupation_type: 'task' | 'chassis';
  task_id?: number;
  chassis_id?: number;
  external_chassis_number?: string;
  external_chassis_company?: string;
  external_chassis_size?: string;
}

// ==================== API Methods ====================

export const yardApi = {
  // ==================== Yards ====================

  async getAllYards(filters?: { status?: string; search?: string }): Promise<Yard[]> {
    const response = await api.get('/yards', { params: filters });
    return response.data || [];
  },

  async getYardById(id: number): Promise<Yard> {
    const response = await api.get(`/yards/${id}`);
    return response.data;
  },

  async createYard(yardData: YardInput): Promise<Yard> {
    const response = await api.post('/yards', yardData);
    return response.data;
  },

  async updateYard(id: number, yardData: YardInput): Promise<Yard> {
    const response = await api.put(`/yards/${id}`, yardData);
    return response.data;
  },

  async deleteYard(id: number): Promise<void> {
    await api.delete(`/yards/${id}`);
  },

  // ==================== Slots ====================

  async getSlots(yardId: number, filters?: {
    status?: string;
    equipmentType?: string;
    dwellTime?: string;
    search?: string;
  }): Promise<YardSlot[]> {
    const response = await api.get(`/yards/${yardId}/slots`, { params: filters });
    const slots: YardSlot[] = response.data || [];
    return slots.map(normalizeSlotPhotoUrls);
  },

  async updateSlot(yardId: number, slotId: string, data: SlotUpdateData): Promise<YardSlot> {
    const response = await api.put(`/yards/${yardId}/slots/${slotId}`, data);
    return normalizeSlotPhotoUrls(response.data);
  },

  async activateSlot(yardId: number, slotId: string): Promise<YardSlot> {
    const response = await api.patch(`/yards/${yardId}/slots/${slotId}/activate`);
    return normalizeSlotPhotoUrls(response.data);
  },

  async occupySlot(yardId: number, slotId: string, data: OccupySlotData): Promise<YardSlot> {
    const response = await api.patch(`/yards/${yardId}/slots/${slotId}/occupy`, data);
    return normalizeSlotPhotoUrls(response.data);
  },

  async releaseSlot(yardId: number, slotId: string): Promise<YardSlot> {
    const response = await api.patch(`/yards/${yardId}/slots/${slotId}/release`);
    return normalizeSlotPhotoUrls(response.data);
  },

  async toggleSlotLock(yardId: number, slotId: string, lock: boolean): Promise<YardSlot> {
    const response = await api.patch(`/yards/${yardId}/slots/${slotId}/lock`, { lock });
    return normalizeSlotPhotoUrls(response.data);
  },

  // ==================== Search (for slot edit modal) ====================

  async getAssignableTasks(yardId: number, search?: string): Promise<AssignableTask[]> {
    const response = await api.get(`/yards/${yardId}/assignable-tasks`, {
      params: search ? { search } : undefined,
    });
    return response.data || [];
  },

  async getAvailableChassis(yardId: number, search?: string): Promise<AvailableChassis[]> {
    const response = await api.get(`/yards/${yardId}/available-chassis`, {
      params: search ? { search } : undefined,
    });
    return response.data || [];
  },

  // ==================== Statistics ====================

  async getYardStats(yardId: number): Promise<YardStats> {
    const response = await api.get(`/yards/${yardId}/stats`);
    return response.data;
  },

  async getOccupiedEquipment(yardId: number): Promise<YardEquipmentListItem[]> {
    const response = await api.get(`/yards/${yardId}/equipment`);
    return response.data || [];
  },

  // ==================== Slot Operations ====================

  async moveEquipment(yardId: number, fromSlotId: string, toSlotId: string): Promise<{ fromSlot: YardSlot; toSlot: YardSlot }> {
    const response = await api.post(`/yards/${yardId}/slots/${fromSlotId}/move`, { toSlotId });
    return {
      fromSlot: normalizeSlotPhotoUrls(response.data.fromSlot),
      toSlot: normalizeSlotPhotoUrls(response.data.toSlot),
    };
  },

  async bulkRelease(yardId: number, slotIds: string[]): Promise<{ released: string[] }> {
    const response = await api.post(`/yards/${yardId}/slots/bulk-release`, { slotIds });
    return response.data;
  },

  async bulkLock(yardId: number, slotIds: string[]): Promise<{ locked: string[] }> {
    const response = await api.post(`/yards/${yardId}/slots/bulk-lock`, { slotIds });
    return response.data;
  },

  async bulkActivate(yardId: number, slotIds: string[]): Promise<{ activated: string[] }> {
    const response = await api.post(`/yards/${yardId}/slots/bulk-activate`, { slotIds });
    return response.data;
  },

  async initializeSlots(yardId: number): Promise<{ message: string; count?: number }> {
    const response = await api.post(`/yards/${yardId}/initialize-slots`);
    return response.data;
  },
};
